/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  search: {
    all: ["search"] as const,
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  powersync: {
    /** Upload queue stats (sync indicator).*/
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** Is there local data? (cleanup button in profile).*/
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (getThemesQuery without pagination; profile + list-dialog SHARE the cache).*/
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated). */
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user. */
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme). */
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  attachments: {
    all: ["attachments"] as const,
    byUser: (userId: string) => ["attachments", userId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector). */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector). */
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync). */
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
    /**
     * Active sessions list of the profile. Prefix of `activeSessionsFor`, to
     * invalidate both with single call after revoke (P25).
     */
    activeSessions: () => ["auth", "activeSessions"] as const,
    activeSessionsFor: (currentSessionId: string | undefined) =>
      ["auth", "activeSessions", currentSessionId] as const,
  },
  // ─── Comandero domain (POS) ────────────────────────────────────────────────
  comandero: {
    organizations: () => ["comandero", "organizations"] as const,
    branches: (orgId: string) => ["comandero", "branches", orgId] as const,
    categories: (orgId: string) => ["comandero", "menu", "categories", orgId] as const,
    products: (orgId: string) => ["comandero", "menu", "products", orgId] as const,
    modifierGroups: (orgId: string) => ["comandero", "menu", "groups", orgId] as const,
    modifierOptions: (groupId: string) => ["comandero", "menu", "options", groupId] as const,
    productModifierGroups: (productId: string) => ["comandero", "menu", "pmg", productId] as const,
    tables: (branchId: string) => ["comandero", "floor", "tables", branchId] as const,
    order: (orderId: string) => ["comandero", "order", orderId] as const,
    orderLines: (orderId: string) => ["comandero", "order", orderId, "lines"] as const,
    orderPayments: (orderId: string) => ["comandero", "order", orderId, "payments"] as const,
    openSession: (branchId: string) => ["comandero", "cashbox", "open", branchId] as const,
    sessions: (branchId: string) => ["comandero", "cashbox", "sessions", branchId] as const,
    // ─── F2: KDS + inventario ────────────────────────────────────────────────
    kitchenOrders: (branchId: string) => ["comandero", "kds", "orders", branchId] as const,
    kitchenLines: (orderId: string) => ["comandero", "kds", "lines", orderId] as const,
    ingredients: (orgId: string) => ["comandero", "inventory", "ingredients", orgId] as const,
    recipeItems: (productId: string) => ["comandero", "inventory", "recipe", productId] as const,
    stockMovements: (ingredientId: string) =>
      ["comandero", "inventory", "movements", ingredientId] as const,
    // ─── F3: compras + empleados + reportes ──────────────────────────────────
    suppliers: (orgId: string) => ["comandero", "purchasing", "suppliers", orgId] as const,
    purchaseOrders: (orgId: string) => ["comandero", "purchasing", "orders", orgId] as const,
    purchaseLines: (poId: string) => ["comandero", "purchasing", "lines", poId] as const,
    employees: (orgId: string) => ["comandero", "staff", "employees", orgId] as const,
    reportOrders: (branchId: string, range: string) =>
      ["comandero", "reports", "orders", branchId, range] as const,
    reportLines: (orgId: string) => ["comandero", "reports", "lines", orgId] as const,
    reportPayments: (orgId: string) => ["comandero", "reports", "payments", orgId] as const,
    // ─── F4: variants + combos + customers/loyalty + reservations ───────────
    variants: (productId: string) => ["comandero", "menu", "variants", productId] as const,
    combos: (orgId: string) => ["comandero", "menu", "combos", orgId] as const,
    comboItems: (comboId: string) => ["comandero", "menu", "combo-items", comboId] as const,
    customers: (orgId: string) => ["comandero", "customers", orgId] as const,
    customer: (customerId: string) => ["comandero", "customer", customerId] as const,
    loyaltyMovements: (customerId: string) =>
      ["comandero", "customers", "loyalty", customerId] as const,
    promotions: (orgId: string) => ["comandero", "promotions", orgId] as const,
    reservations: (branchId: string) => ["comandero", "reservations", branchId] as const,
  },
  // ─── ACTIVITY-CORE: feed L3 + papelera L2 ──────────────────────────────────
  // ALWAYS with userId: isolates cache between accounts in the same browser.
  activity: {
    all: ["activity"] as const,
    feed: (
      userId: string | undefined,
      filters?: { registerSessionId?: string; branchId?: string },
    ) =>
      [
        "activity",
        "feed",
        userId,
        filters?.registerSessionId ?? null,
        filters?.branchId ?? null,
      ] as const,
    entity: (userId: string | undefined, entityType: string, entityId: string) =>
      ["activity", "entity", userId, entityType, entityId] as const,
  },
  gamification: {
    all: ["gamification"] as const,
    /** Game state (XP/level/streak/badges) derived from the activity feed. */
    player: (userId: string | undefined) => ["gamification", "player", userId] as const,
  },
  trash: {
    all: ["trash"] as const,
    items: (userId: string | undefined, orgId: string | undefined) =>
      ["trash", "items", userId, orgId] as const,
  },
};
